import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

const BeforeMock = () => (
    <div className="h-full w-full bg-[#c9c9c9] p-5 sm:p-8" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
        <div className="border-2 border-[#8a8a8a] bg-[#e8e8e8] px-3 py-2 text-center shadow-[inset_1px_1px_#ffffff,inset_-1px_-1px_#7a7a7a]">
            <p className="text-base font-bold text-[#0000cc] underline sm:text-2xl">Welcome To Bob&apos;s Business Home Page!!!</p>
            <p className="mt-1 text-[9px] text-[#555] sm:text-[11px]">Best viewed at 800x600 &bull; Est. 2004 &bull; Sign my guestbook</p>
        </div>
        <div className="mt-3 flex gap-3 sm:mt-4">
            <div className="w-1/4 border border-[#999] bg-[#dcdcdc] p-2 text-[9px] leading-5 text-[#0000cc] underline sm:text-[11px]">
                Home<br />About Us<br />Photo&apos;s<br />Links<br />Guestbook<br />Email Me
            </div>
            <div className="flex-1 border border-[#999] bg-white p-3">
                <p className="text-[10px] leading-relaxed text-[#222] sm:text-xs">
                    Hello and thank you for visiting my web site. We do quality work at fair prices.
                    Please call for free estimate. Check back soon for updates!!
                </p>
                <span className="mt-3 inline-block border-2 border-[#cfcfcf] bg-[#d4d0c8] px-3 py-1 text-[9px] font-bold text-black shadow-[inset_1px_1px_#ffffff,inset_-1px_-1px_#6a6a6a] sm:text-[11px]">
                    CLICK HERE
                </span>
                <p className="mt-3 text-[8px] text-[#888] sm:text-[10px]">You are visitor number: 0004231</p>
            </div>
        </div>
    </div>
);

const AfterMock = () => (
    <div className="bg-grid-faint h-full w-full bg-[#05070B] p-5 sm:p-8">
        <div className="flex items-center justify-between">
            <span className="h-2.5 w-2.5 rounded-full bg-sky-400 shadow-[0_0_14px_rgba(56,189,248,0.7)]" />
            <div className="flex gap-2.5">
                <span className="h-1 w-8 rounded bg-white/20" />
                <span className="h-1 w-8 rounded bg-white/20" />
                <span className="h-1 w-8 rounded bg-white/20" />
            </div>
        </div>
        <p className="mt-6 font-mono text-[8px] uppercase tracking-[0.35em] text-sky-400 sm:mt-10 sm:text-[11px]">
            The Footman Rebuild
        </p>
        <p className="mt-2 font-display text-xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-4xl">
            SAME BUSINESS.<br />
            <span className="text-electric-grad">NEW STANDARD.</span>
        </p>
        <span className="mt-4 inline-flex rounded-full bg-[#0066FF] px-4 py-1.5 font-display text-[9px] font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_24px_rgba(0,102,255,0.45)] sm:mt-6 sm:px-5 sm:py-2 sm:text-[11px]">
            Get A Quote
        </span>
        <div className="mt-6 grid grid-cols-3 gap-2 sm:mt-10 sm:gap-3">
            {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-2 sm:p-3">
                    <div className="mb-1.5 h-4 rounded bg-gradient-to-br from-sky-400/40 to-[#0066FF]/40 sm:h-6" />
                    <div className="h-1 w-full rounded bg-white/15" />
                    <div className="mt-1 h-1 w-2/3 rounded bg-white/15" />
                </div>
            ))}
        </div>
    </div>
);

export const Transform = () => {
    const ref = useRef(null);
    const [pos, setPos] = useState(58);
    const [dragging, setDragging] = useState(false);

    const update = useCallback((clientX) => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
    }, []);

    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 4));
        else if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 4));
        else if (e.key === "Home") setPos(4);
        else if (e.key === "End") setPos(96);
        else return;
        e.preventDefault();
    };

    return (
        <section id="transform" className="relative py-24 md:py-36" data-testid="transform-section">
            <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-[#0066FF]/40 to-transparent" aria-hidden="true" />
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Reveal>
                            <Eyebrow>The Glow-Up</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-[#0A0F1D] sm:text-5xl lg:text-6xl">
                                FROM DATED<br />TO <span className="text-electric-grad">UNFORGETTABLE.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="max-w-sm text-sm leading-relaxed text-slate-600 sm:text-base">
                            Drag the handle. On the left, the web of twenty years ago. On the right, what your
                            business looks like after a Footman rebuild.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.15} className="mt-14">
                    <div
                        ref={ref}
                        role="slider"
                        tabIndex={0}
                        aria-label="Before and after website redesign comparison"
                        aria-valuenow={Math.round(pos)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        data-testid="before-after-slider"
                        className="relative aspect-[4/3] cursor-ew-resize touch-none select-none overflow-hidden border-2 border-[#0A0F1D] shadow-[12px_12px_0_rgba(0,102,255,0.7)] sm:aspect-[16/8]"
                        onPointerDown={(e) => {
                            setDragging(true);
                            e.currentTarget.setPointerCapture(e.pointerId);
                            update(e.clientX);
                        }}
                        onPointerMove={(e) => dragging && update(e.clientX)}
                        onPointerUp={() => setDragging(false)}
                        onPointerCancel={() => setDragging(false)}
                        onKeyDown={onKeyDown}
                    >
                        <div className="absolute inset-0">
                            <AfterMock />
                        </div>
                        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                            <BeforeMock />
                        </div>

                        <span
                            className={`absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-300 backdrop-blur transition-opacity duration-300 ${pos < 18 ? "opacity-0" : "opacity-100"}`}
                        >
                            Before
                        </span>
                        <span
                            className={`absolute right-4 top-4 rounded-full bg-[#0066FF]/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white backdrop-blur transition-opacity duration-300 ${pos > 82 ? "opacity-0" : "opacity-100"}`}
                        >
                            After
                        </span>

                        <div
                            data-testid="before-after-handle"
                            className="absolute inset-y-0 z-10"
                            style={{ left: `${pos}%` }}
                            aria-hidden="true"
                        >
                            <div className="absolute inset-y-0 -translate-x-1/2">
                                <div className="h-full w-px bg-sky-400 shadow-[0_0_16px_rgba(56,189,248,0.8)]" />
                            </div>
                            <div
                                className={`absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-sky-400/60 bg-[#05070B]/90 text-sky-300 backdrop-blur transition-[box-shadow,transform] duration-300 ${
                                    dragging ? "scale-110 shadow-[0_0_36px_rgba(0,102,255,0.6)]" : "shadow-[0_0_20px_rgba(0,102,255,0.35)]"
                                }`}
                            >
                                <MoveHorizontal className="h-5 w-5" />
                            </div>
                        </div>
                    </div>
                    <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        Concept demonstration &mdash; drag, or use arrow keys
                    </p>
                </Reveal>
            </div>
        </section>
    );
};
